import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";

import PeriodList from "./PeriodList";
import MultiPickerField from "./MultiPickerField";
import { DAY_KEYS, DAY_LABELS, valueToLabel, minutesToLabel } from "./timeUtils";

// Complete From/To ranges for a set of periods, as a display string, or "" if none.
const rangesText = (periods) =>
  (periods || [])
    .filter((p) => p.from && p.to)
    .map((p) => `${valueToLabel(p.from)} – ${valueToLabel(p.to)}`)
    .join(", ");

/**
 * Renders the preferences inputs (sports/services/trainers picker fields,
 * preferred-time editor with simple + advanced modes, date of birth) bound to a
 * `usePreferencesForm` instance. Used by both the signup PreferencesView and the
 * dashboard Preferences tab. Selection fields open picker dialogs (full-screen
 * on phones) instead of inline dropdowns, so the mobile keyboard stays closed
 * and dismissal is an explicit Done button.
 */
export default function PreferencesFields({ form }) {
  const theme = useTheme();
  const fullScreenTimeDialog = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    sportOptions,
    serviceOptions,
    trainerOptions,
    locationOptions,
    slots,
    openWindow,
    selectedSports,
    setSelectedSports,
    selectedServices,
    setSelectedServices,
    selectedTrainers,
    setSelectedTrainers,
    selectedLocations,
    setSelectedLocations,
    durationOptions,
    selectedDurations,
    setSelectedDurations,
    weekdayPeriods,
    weekendPeriods,
    perDayPeriods,
    useAdvanced,
    timeDialog,
    timeDraft,
    openPerDayDialog,
    openScopeDialog,
    closeTimeDialog,
    setDraftDay,
    setDraftScope,
    applyTimeDialog,
    birthDate,
    setBirthDate
  } = form;

  // What the active mode currently has set — shown as a compact summary above the
  // two buttons so the client sees their selection without opening a modal.
  const timeSummary = useAdvanced
    ? DAY_KEYS.map((day) => ({ label: DAY_LABELS[day], value: rangesText(perDayPeriods[day]) })).filter((l) => l.value)
    : [
        { label: "Weekdays (Mon–Fri)", value: rangesText(weekdayPeriods) },
        { label: "Weekend (Sat–Sun)", value: rangesText(weekendPeriods) }
      ].filter((l) => l.value);

  return (
    <>
      <Grid container spacing={2.5}>
        {sportOptions.length > 0 && (
          <Grid item xs={12}>
            <MultiPickerField
              label="Preferred sports"
              options={sportOptions}
              value={selectedSports}
              onChange={setSelectedSports}
              idKey="sportId"
              labelKey="sportName"
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <MultiPickerField
            label="Preferred services"
            options={serviceOptions}
            value={selectedServices}
            onChange={setSelectedServices}
            idKey="serviceId"
            labelKey="serviceName"
          />
        </Grid>

        <Grid item xs={12}>
          <MultiPickerField
            label="Preferred trainers"
            options={trainerOptions}
            value={selectedTrainers}
            onChange={setSelectedTrainers}
            idKey="trainerId"
            labelKey="trainerName"
          />
        </Grid>

        {locationOptions.length > 0 && (
          <Grid item xs={12}>
            <MultiPickerField
              label="Preferred locations"
              options={locationOptions}
              value={selectedLocations}
              onChange={setSelectedLocations}
              idKey="locationId"
              labelKey="locationName"
            />
          </Grid>
        )}

        {durationOptions.length > 1 && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Preferred session length
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {durationOptions.map((d) => {
                const selected = selectedDurations.includes(d);
                return (
                  <Chip
                    key={d}
                    label={`${d} min`}
                    color={selected ? "primary" : "default"}
                    variant={selected ? "filled" : "outlined"}
                    onClick={() =>
                      setSelectedDurations(selected ? selectedDurations.filter((x) => x !== d) : [...selectedDurations, d].sort((a, b) => a - b))
                    }
                  />
                );
              })}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
              Leave empty to see every length.
            </Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={600} mb={openWindow.bounded ? 0.5 : 1}>
            Preferred time
          </Typography>
          {openWindow.bounded && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Club hours: {minutesToLabel(openWindow.from)} – {minutesToLabel(openWindow.to)}
            </Typography>
          )}
          {timeSummary.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              {timeSummary.map((line) => (
                <Typography key={line.label} variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{line.label}:</strong> {line.value}
                </Typography>
              ))}
            </Box>
          )}
          {/* A button turns filled only once its mode actually has times set.
              The modes are mutually exclusive, so at most one is ever filled. */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
            <Button
              variant={useAdvanced && timeSummary.length > 0 ? "contained" : "outlined"}
              size="small"
              disableElevation
              onClick={openPerDayDialog}
              sx={{ minHeight: "2.75rem", lineHeight: 1.3 }}
            >
              Different time each day
            </Button>
            <Button
              variant={!useAdvanced && timeSummary.length > 0 ? "contained" : "outlined"}
              size="small"
              disableElevation
              onClick={openScopeDialog}
              sx={{ minHeight: "2.75rem", lineHeight: 1.3 }}
            >
              Weekday &amp; weekend
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <MobileDatePicker
              label="Date of birth"
              value={birthDate}
              onChange={setBirthDate}
              openTo="year"
              views={["year", "month", "day"]}
              disableFuture
              format="dd LLL yyyy"
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Dialog open={timeDialog !== null} onClose={closeTimeDialog} fullScreen={fullScreenTimeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{timeDialog === "perday" ? "Preferred time per day" : "Preferred time"}</DialogTitle>
        <DialogContent dividers>
          {openWindow.bounded && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              Club hours: {minutesToLabel(openWindow.from)} – {minutesToLabel(openWindow.to)}
            </Typography>
          )}
          {timeDialog === "perday" &&
            timeDraft &&
            DAY_KEYS.map((day, idx) => (
              <Box key={day} sx={{ mb: 2 }}>
                <PeriodList label={DAY_LABELS[day]} slots={slots} periods={timeDraft[day] || []} onChange={(periods) => setDraftDay(day, periods)} />
                {idx < DAY_KEYS.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          {timeDialog === "scope" && timeDraft && (
            <Box display="flex" flexDirection="column" gap={2}>
              <PeriodList
                label="Weekdays (Mon–Fri)"
                slots={slots}
                periods={timeDraft.weekday || []}
                onChange={(periods) => setDraftScope("weekday", periods)}
              />
              <PeriodList
                label="Weekend (Sat–Sun)"
                slots={slots}
                periods={timeDraft.weekend || []}
                onChange={(periods) => setDraftScope("weekend", periods)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTimeDialog}>Cancel</Button>
          <Button variant="contained" onClick={applyTimeDialog}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
